import {
  computed,
  effect,
  inject,
  Injectable,
  linkedSignal,
  resource,
} from '@angular/core'
import { HttpService } from './http.service'

@Injectable({
  providedIn: 'root',
})
export class AgencyService {
  private readonly selectedAgencyStorageKey = 'tractionTravel.selectedAgencyID'
  private http = inject(HttpService)

  agenciesResource = resource({
    loader: ({ abortSignal }) => {
      return this.http.get<Agency[]>('/agencies', abortSignal)
    },
  })
  selectedAgencyID = linkedSignal(() => {
    const agencies = this.agenciesResource.value()
    if (!agencies || agencies.length === 0) {
      return ''
    }

    const storedAgencyID = this.getStoredSelectedAgencyID()
    if (
      storedAgencyID &&
      agencies.some(agency => agency.id === storedAgencyID)
    ) {
      return storedAgencyID
    }

    return agencies[0]?.id || ''
  })

  constructor() {
    effect(() => {
      const selectedAgencyID = this.selectedAgencyID()

      if (selectedAgencyID) {
        this.storeSelectedAgencyID(selectedAgencyID)
      }
    })
  }

  agencyCenter = computed(() => {
    const agencies = this.agenciesResource.value()
    if (!agencies || agencies.length === 0) {
      return [0, 0] as [number, number]
    }
    const selectedAgencyID = this.selectedAgencyID()
    const selectedAgency = agencies.find(a => a.id === selectedAgencyID)
    return selectedAgency?.center
      ? ([selectedAgency.center.longitude, selectedAgency.center.latitude] as [
          number,
          number,
        ])
      : ([0, 0] as [number, number])
  })

  hasWazeData = computed(() => {
    const agencies = this.agenciesResource.value()
    if (!agencies || agencies.length === 0) {
      return false
    }
    const selectedAgencyID = this.selectedAgencyID()
    const selectedAgency = agencies.find(a => a.id === selectedAgencyID)
    return selectedAgency?.hasWazeData || false
  })

  useMetric = computed(() => {
    const agencies = this.agenciesResource.value()
    if (!agencies || agencies.length === 0) {
      return false
    }
    const selectedAgencyID = this.selectedAgencyID()
    const selectedAgency = agencies.find(a => a.id === selectedAgencyID)
    return selectedAgency?.useMetric || false
  })

  reportFrequencyId = computed(() => {
    const agencies = this.agenciesResource.value()
    if (!agencies || agencies.length === 0) {
      return ''
    }
    const selectedAgencyID = this.selectedAgencyID()
    const selectedAgency = agencies.find(a => a.id === selectedAgencyID)
    return selectedAgency?.reportFrequencyId || ''
  })

  private getStoredSelectedAgencyID() {
    try {
      return window.localStorage.getItem(this.selectedAgencyStorageKey)
    } catch {
      return null
    }
  }

  private storeSelectedAgencyID(selectedAgencyID: string) {
    try {
      window.localStorage.setItem(
        this.selectedAgencyStorageKey,
        selectedAgencyID
      )
    } catch {
      // Ignore storage failures so agency selection still works in-memory.
    }
  }
}

type Agency = {
  id: string
  center: LatLon
  displayName: string
  hasWazeData: boolean
  useMetric: boolean
  reportFrequencyId: string
}

type LatLon = {
  latitude: number
  longitude: number
}
